<template>
  <div
    :id="figId + '-wrapper'"
    :class="[figId]"
    :key="figId"
    class="fixed-grid min-width pointer-minus"
  >
    <div class="section">
      <div class="heading section-break">
        <p class="overline small text-second">Iteration 1</p>
        <h3 class="headline">Main Workflow</h3>
      </div>
      <p class="content">
        Starting from a low-fi mockup made by the product team beforehand, I
        designed the first iteraction of key screens. Being in a product line
        that moves relatively fast, this could allow me to elicit feedback from
        both the product team and the development team.
      </p>
      <p class="content">
        In this iteration, my key focus was the aforementioned main workflow
        which is fairly similar to that of the web platform:
      </p>
      <ol class="content">
        <li>
          Developer account
          <strong class="highlight">login</strong>;
        </li>
        <li>
          Component
          <strong class="highlight">package</strong> folder (sync
          automatically);
        </li>
        <li>
          Component
          <strong class="highlight">creation</strong> & import;
        </li>
        <li>
          Component local
          <strong class="highlight">preview</strong> and testing.
        </li>
      </ol>
    </div>
    <figure class="c-1--1 iteration-1">
      <figure class="c-3 c-span2 r-1 ali-low-login">
        <img
          src="@/assets/work/alibaba/content/iteration/low-login.png"
          alt="Login Screen - 1st Iteration"
        />
      </figure>
      <figure class="c-3 c-span2 r-2 ali-low-package">
        <img
          src="@/assets/work/alibaba/content/iteration/low-package.png"
          alt="Component Package Screen - 1st Iteration"
        />
        <figcaption class="xsmall text-second">
          <strong>Login & Component Package</strong>
          <br />Log in to the developer account and manage synced component
          ackage folder.
        </figcaption>
      </figure>
      <figure class="c-5 c-span2 r-2 ali-low-create">
        <img
          src="@/assets/work/alibaba/content/iteration/low-create-comp.png"
          alt="Component Creation Screen - 1st Iteration"
        />
        <figcaption class="xsmall text-second">
          <strong>Create Component</strong>
          <br />Create / import component in the package folder and
          automatically sync component.
        </figcaption>
      </figure>
      <figure class="c-7 c-span2 r-2 ali-low-preview">
        <img
          src="@/assets/work/alibaba/content/iteration/low-preview.png"
          alt="Component Preview Screen - 1st Iteration"
        />
        <figcaption class="xsmall text-second">
          <strong>Preview Component</strong>
          <br />Preview component with a 4-tab properties panel (final tab being
          testing panel).
        </figcaption>
      </figure>
    </figure>
    <div class="section">
      <div class="heading section-break">
        <p class="overline small text-second">Iteration 2</p>
        <h3 class="headline">Visual & Interaction Design Refinement</h3>
      </div>
      <p class="content">
        In this iteration, I started to incorporate visual guidelines from the
        visual design team. Meanwhile, I was able to conduct several contextual
        inquiries with current users between iterations to learn more about
        their workflows and feedback.
      </p>
      <p class="content">
        Based on these feedback and insights, I made several changes to the
        design:
      </p>
      <ol class="content">
        <li>
          Add dedicated button for component
          <strong class="highlight">import</strong>
          and
          <strong class="highlight">recent</strong>
          files to account for common alternative workflows;
        </li>
        <li>
          Tiered status bar → message flash → modal alert designs for
          <strong class="highlight">error state</strong>
          handling;
        </li>
        <li>
          Refined
          <strong class="highlight">component preview</strong>
          designs (e.g. canvas controls, debug console, inspect).
        </li>
      </ol>
    </div>
    <figure class="c-1--1 iteration-2">
      <figure class="c-3 c-span2 r-1 ali-mid-package">
        <img
          src="@/assets/work/alibaba/content/iteration/mid-package.png"
          alt="Component Package Screen - 2nd Iteration"
          class="mid-pad"
        />
        <figcaption class="xsmall text-second">
          <strong>Component Package</strong>
          <br />Incorporate visual guides & iconset to the mid-fi designs; Add
          recent components.
        </figcaption>
      </figure>
      <figure class="c-5 c-span2 r-1 ali-mid-import">
        <img
          src="@/assets/work/alibaba/content/iteration/mid-import-comp.png"
          alt="Component Import Screen - 2nd Iteration"
          class="mid-pad"
        />
        <figcaption class="xsmall text-second">
          <strong>Create / Import Component</strong>
          <br />Add dedicated import button to better accommodate an alternative
          workflow.
        </figcaption>
      </figure>
      <figure class="c-7 c-span2 r-1 ali-mid-test">
        <img
          src="@/assets/work/alibaba/content/iteration/mid-test.png"
          alt="Component Preview Screen (Test Case) - 2nd Iteration"
        />
        <figcaption class="xsmall text-second hide">
          <strong>Preview Component (Test Case)</strong>
          <br />Add detailed designs and interactions for component test cases
          and inspect window.
        </figcaption>
      </figure>
      <figure class="c-7 c-span2 r-1 ali-mid-console">
        <img
          src="@/assets/work/alibaba/content/iteration/mid-console.png"
          alt="Component Preview Screen (Console) - 2nd Iteration"
        />
        <figcaption class="xsmall text-second hide">
          <strong>Preview Component (Console)</strong>
          <br />Add detailed designs and interactions for component testing
          console.
        </figcaption>
      </figure>
      <figure class="c-7 c-span2 r-1 ali-mid-preview">
        <img
          src="@/assets/work/alibaba/content/iteration/mid-preview.png"
          alt="Component Preview Screen - 2nd Iteration"
        />
        <figcaption class="xsmall text-second">
          <strong>Preview Component</strong>
          <br />Add detailed designs and interactions for each tab of component
          properties panel.
        </figcaption>
      </figure>
    </figure>
    <div class="section">
      <div class="heading section-break">
        <p class="overline small text-second">Iteration 3</p>
        <h3 class="headline">Final Design</h3>
      </div>
      <p class="content">
        Through communication with the development team, I realized there was a
        mismatch between the expectation of component sync the product team had
        previously envisioned and the feasibility of implementing such a design
        in a local environment.
      </p>
      <p class="content">
        This called for a rethink for the design of the local component
        management part of the product. I decided to re-analyze the previous
        <strong class="highlight">contextual inquiries</strong>
        and do a short
        <strong class="highlight">comparative analysis</strong>
        of similar development tools for insights.
      </p>
    </div>
    <figure class="c-1--1 iteration-3">
      <figure class="c-3 c-span2 ali-hi-login">
        <img
          src="@/assets/work/alibaba/content/key-screens/window-log-in.png"
          alt="Login / Workspace Panel - Final Iteration"
        />
        <figcaption class="xsmall text-second">
          <strong>Login & Workspace Panel</strong>
          <br />Switch to workspace mental model and incorporate workspace
          management to login screen.
        </figcaption>
      </figure>
      <figure class="c-5 c-span2 ali-hi-workspace">
        <img
          src="@/assets/work/alibaba/content/key-screens/window-workspace.png"
          alt="Workspace Screen - Final Iteration"
          class="mid-pad"
        />
        <figcaption class="xsmall text-second">
          <strong>Create / Import Component</strong>
          <br />Switch to workspace mental model and add workspace panel to the
          left of component tabs.
        </figcaption>
      </figure>
      <figure class="c-7 c-span2 ali-hi-preview">
        <img
          src="@/assets/work/alibaba/content/key-screens/window.png"
          alt="Component Preview Screen - Final Iteration"
          class="mid-pad"
        />
        <figcaption class="xsmall text-second">
          <strong>Preview Component</strong>
          <br />Split preview and testing mode and add deploy panel to
          distinguish local workspace from online package.
        </figcaption>
      </figure>
    </figure>
    <div class="section">
      <p class="content">
        Based on these research, I redesigned the component package section of
        the product to switch to a mental model of
        <strong class="highlight">workspace</strong>
        instead of a
        <strong class="highlight">synced package</strong>
        to better align user expectations with the intended functionality of the
        product, which was mainly to serve as a
        <strong class="highlight">local</strong> testing environment. I delayed
        the process for deploy / upload later in the process to avoid some of
        the issues that may occur in a synced environment and at the same time
        enabling more flexibility for users.
      </p>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/styles/base.scss';

.ali-iterations {
  display: grid;
  grid-column: 1 / -1;
  grid-column-gap: $column-gap;
  grid-template-columns: $body-padding - $column-gap repeat(8, 1fr) $body-padding - $column-gap;

  & > .section {
    max-width: 100vw !important;
    padding: 0;
  }

  .mid-pad {
    padding-bottom: (248 - 452 * 318 / 600) / 318 * 100%;
  }

  .iteration-1 {
    margin-top: $line-height * -6;
  }

  .ali-low-login {
    margin-bottom: -128 / 248 * 100%;
  }
  .ali-mid-test {
    z-index: -1;
    width: 406 / 318 * 100%;
    transform: translateX(180 / 406 * 100%);
  }
  .ali-mid-console {
    z-index: -1;
    transform: translateX(90 / 318 * 100%);
  }
  .ali-hi-login img {
    width: 189 / 318 * 100%;
    padding-right: (1 - 189 / 318) * 100%;
    padding-bottom: (248 - 189 * 720 / 1202) / 318 * 100%;
  }
  .hide {
    visibility: hidden;
  }
}

.ali-iterations-text-body {
  @extend .text-title;
  font-weight: 500 !important;
  letter-spacing: -0.69;
  fill: $alibaba-theme;
}
.ali-iterations-text-strike {
  @extend .text-title;
  font-weight: 500 !important;
  letter-spacing: -0.69;
  fill: $text-light-second;
  text-decoration: line-through;
}
.ali-iterations-text-title {
  @extend .text-title;
  @extend .large;
  letter-spacing: -0.69;
  fill: $alibaba-tonedown;
}
</style>

<script>
// import anime from 'animejs'

export default {
  name: 'ali-iterations',
  data() {
    return {
      svgLib: {
        // palette: require('@/assets/work/alibaba/content/color-palette.svg')
      },
      figId: 'ali-iterations'
    }
  },
  components: {},
  computed: {},
  methods: {},
  mounted() {}
}
</script>
